<template>
  <div :class="[$style.leadQualificationBox]">
    <HgSelect
      v-model="formState.employmentStatus"
      name="employmentStatus"
      :options="employmentStatusOptions"
      :label="employmentStatusLabel"
      @update:modelValue="handleEmploymentStatusSelectionChanged"
    />
    <HgSelect
      v-model="formState.monthlyIncome"
      name="monthlyIncome"
      :options="monthlyIncomeOptions"
      :label="monthlyIncomeLabel"
      @update:modelValue="handleMonthlyIncomeSelectedValue"
    />
    <HgCurrencyInput
      v-model="formState.equityCapital"
      name="equityCapital"
      :label="equityCapitalLabel"
      inputmode="numeric"
      autocomplete="off"
      @update:modelValue="handleEquityCapitalValueChanged"
    ></HgCurrencyInput>
  </div>
</template>

<script lang="ts" setup>
import {
  HgCurrencyInput,
  HgCurrencyInputModel,
  HgSelect,
  HgSelectOptions,
} from '@smg-real-estate/domus';
import { useVuelidate } from '@vuelidate/core';
import { computed, reactive } from 'vue';

import { InteractionEvent, InteractionGoal } from '@/helpers/GTM/GTMParameters';
import { trackEventHarmonized } from '@/helpers/GTM/GTMTrack';

import { useI18n } from 'vue-i18n';
import { generateMonthlyIncomeSelectOptions } from './priceOptions';
import { generateEmploymentStatusSelectOptions } from './types';

const INPUT_MAX_VALUE = 99999999;

const { locale, t } = useI18n();

defineOptions({
  name: 'LeadQualificationFieldsForBuy',
});

const emit = defineEmits<{
  onEmploymentStatusChanged: [value: string];
  onMonthlyIncomeChanged: [value: string];
  onEquityCapitalChanged: [value: number];
}>();

export interface Props {
  initialEmploymentStatus: string | undefined;
  initialMonthlyIncome: string | undefined;
  initialEquityCapital: number | undefined;
}

const props = withDefaults(defineProps<Props>(), {
  initialEmploymentStatus: '',
  initialMonthlyIncome: '',
  initialEquityCapital: undefined,
});

const formState = reactive({
  equityCapital: {
    price: props.initialEquityCapital,
    currency: 'CHF',
  },
  employmentStatus: props.initialEmploymentStatus,
  monthlyIncome: props.initialMonthlyIncome,
});

const rules = computed(() => ({
  equityCapital: {
    price: {
      inputted: (value: string) => value !== '',
    },
  },
}));

const employmentStatusLabel = computed(() =>
  t('contactForm.leadQualification.buy.employmentStatus.label'),
);
const monthlyIncomeLabel = computed(() =>
  t('contactForm.leadQualification.buy.householdIncome.label'),
);
const equityCapitalLabel = computed(() =>
  t('contactForm.leadQualification.buy.equityCapital.label'),
);
const employmentStatusOptions = computed<HgSelectOptions[]>(() =>
  generateEmploymentStatusSelectOptions(t),
);
const monthlyIncomeOptions = computed<HgSelectOptions[]>(() =>
  generateMonthlyIncomeSelectOptions(t, locale.value),
);

useVuelidate(rules, formState, { $autoDirty: true });

function handleEmploymentStatusSelectionChanged(value: string): void {
  emit('onEmploymentStatusChanged', value);
  trackEventHarmonized(InteractionEvent.PDP_INTERACTION, {
    h_event_category_006: InteractionEvent.PDP_INTERACTION,
    h_event_action_007: InteractionGoal.OPEN_DROPDOWN,
    h_event_label_008: 'employment_status',
  });
}
function handleMonthlyIncomeSelectedValue(selectedValue: string) {
  emit('onMonthlyIncomeChanged', selectedValue);
  trackEventHarmonized(InteractionEvent.PDP_INTERACTION, {
    h_event_category_006: InteractionEvent.PDP_INTERACTION,
    h_event_action_007: InteractionGoal.OPEN_DROPDOWN,
    h_event_label_008: 'financial_profile',
  });
}
function handleEquityCapitalValueChanged(val: HgCurrencyInputModel) {
  const newVal = val.price > INPUT_MAX_VALUE ? INPUT_MAX_VALUE : val.price;
  formState.equityCapital.price = newVal;
  emit('onEquityCapitalChanged', newVal);
  trackEventHarmonized(InteractionEvent.PDP_INTERACTION, {
    h_event_category_006: InteractionEvent.PDP_INTERACTION,
    h_event_action_007: InteractionGoal.INPUT_LEAD_QUALIFICATION_INFO,
    h_event_label_008: 'equity_capital',
  });
}
</script>

<style lang="scss" module>
@import '@/assets/variables';

.leadQualificationBox {
  display: flex;
  flex-direction: column;
  gap: $space-100;
  overflow: hidden;

  fieldset {
    margin: 0;
  }
}
</style>
